import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { ICrmError, IPublicBooking, IPublicBookingProfile, ISlots } from "./types";

const bookUrl = `${environment.v2Url}/public/book`;
/** A rep slug, or `team/<slug>` for a team link. */
const path = (slug: string): string => slug.split('/').map(encodeURIComponent).join('/');

const handle = (err: any): ICrmError => {
    const status = err?.response?.status ?? 0;
    return {
        ok: false,
        status,
        message: err?.response?.data?.message
            || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
    };
};

/**
 * The public booking page's API. No login, no auth header: anyone with the
 * link may look at openings and book one. Plain functions, not a hook, so the
 * page works outside the dashboard shell.
 */
export const bookingApi = {
    profile: async (slug: string): Promise<IPublicBookingProfile | ICrmError> => {
        try { const r: AxiosResponse<IPublicBookingProfile> = await axios.get(`${bookUrl}/${path(slug)}`); return r.data; } catch (err: any) { return handle(err); }
    },
    slots: async (slug: string, typeId: number, fromIso: string, toIso: string): Promise<ISlots | ICrmError> => {
        try {
            const r: AxiosResponse<ISlots> = await axios.get(`${bookUrl}/${path(slug)}/slots`, { params: { type: typeId, from: fromIso, to: toIso } });
            return r.data;
        } catch (err: any) { return handle(err); }
    },
    /** `submission_id` comes from the website's demo form and links this booking to that request and its lead. */
    book: async (slug: string, input: { meeting_type_id: number; start_at: string; invitee_name: string; invitee_email: string; invitee_time_zone: string; notes?: string | null; submission_id?: string | null }): Promise<IPublicBooking | ICrmError> => {
        try { const r: AxiosResponse<IPublicBooking> = await axios.post(`${bookUrl}/${path(slug)}`, input); return r.data; } catch (err: any) { return handle(err); }
    },
    /**
     * Someone booking without the website's enquiry tells us who they are first (schema v2, intent "booking"),
     * so the booking lands on their lead with prep. Returns the submission id to book with.
     */
    bookingIntake: async (payload: Record<string, unknown>): Promise<true | ICrmError> => {
        try { await axios.post(`${environment.v2Url}/public/demo-requests`, payload); return true; } catch (err: any) { return handle(err); }
    },
    /** The "Get started" form (schema v2, intent "signup" or "booking"): the CRM saves it and says where to go next. */
    getStarted: async (payload: Record<string, unknown>): Promise<{ next?: { kind: 'book' | 'self_serve'; team_slug?: string } } | ICrmError> => {
        try {
            const r: AxiosResponse<{ accepted: true; next?: { kind: 'book' | 'self_serve'; team_slug?: string } }> =
                await axios.post(`${environment.v2Url}/public/demo-requests`, payload, { timeout: 20_000 });
            return { next: r.data?.next };
        } catch (err: any) { return handle(err); }
    },
    manage: async (token: string): Promise<IPublicBooking | ICrmError> => {
        try { const r: AxiosResponse<IPublicBooking> = await axios.get(`${bookUrl}/manage/${encodeURIComponent(token)}`); return r.data; } catch (err: any) { return handle(err); }
    },
    cancel: async (token: string, reason?: string | null): Promise<IPublicBooking | ICrmError> => {
        try { const r: AxiosResponse<IPublicBooking> = await axios.post(`${bookUrl}/manage/${encodeURIComponent(token)}/cancel`, { reason: reason ?? null }); return r.data; } catch (err: any) { return handle(err); }
    },
    /** The invitee's "Confirm my meeting" tap. Idempotent. */
    confirm: async (token: string): Promise<IPublicBooking | ICrmError> => {
        try { const r: AxiosResponse<IPublicBooking> = await axios.post(`${bookUrl}/manage/${encodeURIComponent(token)}/confirm`, {}); return r.data; } catch (err: any) { return handle(err); }
    },
    /** Apple / other calendars: the meeting as an .ics file. */
    icsUrl: (token: string): string => `${bookUrl}/manage/${encodeURIComponent(token)}/event.ics`,
    reschedule: async (token: string, startIso: string): Promise<IPublicBooking | ICrmError> => {
        try { const r: AxiosResponse<IPublicBooking> = await axios.post(`${bookUrl}/manage/${encodeURIComponent(token)}/reschedule`, { start_at: startIso }); return r.data; } catch (err: any) { return handle(err); }
    }
};
